/**
 * Grouping types for the Show By page
 */

export type GroupingMode =
  | 'ticker'        // No grouping (default)
  | 'underlying'    // Group by underlying ticker
  | 'sector'        // Group by business sector
  | 'industry'      // Group by industry
  | 'assetType'     // ETF vs Stock
  | 'dividend'      // Dividend payers vs Non-dividend payers
  | 'performance';  // By CAGR tier (high/medium/low/negative)

export interface GroupingOption {
  id: GroupingMode;
  label: string;
  description: string;
}

export const GROUPING_OPTIONS: GroupingOption[] = [
  {
    id: 'ticker',
    label: 'By Ticker',
    description: 'No grouping - show all tickers individually',
  },
  {
    id: 'underlying',
    label: 'By Underlying',
    description: 'Group derivative ETFs with their underlying stock',
  },
  {
    id: 'sector',
    label: 'By Sector',
    description: 'Group by business sector (Technology, Healthcare, etc.)',
  },
  {
    id: 'industry',
    label: 'By Industry',
    description: 'Group by industry classification within sectors',
  },
  {
    id: 'assetType',
    label: 'By Asset Type',
    description: 'Group by ETF vs Stock',
  },
  {
    id: 'dividend',
    label: 'By Dividend Status',
    description: 'Group dividend payers vs non-dividend payers',
  },
  {
    id: 'performance',
    label: 'By Performance',
    description: 'Group by CAGR tier (High >20%, Medium 5-20%, Low <5%, Negative)',
  },
];

export interface GroupAggregates {
  count: number;
  avgTotalReturn: number;
  avgCAGR: number;
  totalDividends: number;
  avgDividendYield: number | null;
  avgVolatility: number | null;
  bestPerformer: string | null;
  worstPerformer: string | null;
}

export interface GroupedTickerData<T> {
  groupKey: string;
  groupLabel: string;
  tickers: T[];
  aggregates: GroupAggregates;
}

// Performance tier thresholds
export const PERFORMANCE_TIERS = {
  HIGH: { min: 20, label: 'High (>20% CAGR)' },
  MEDIUM: { min: 5, max: 20, label: 'Medium (5-20% CAGR)' },
  LOW: { min: 0, max: 5, label: 'Low (0-5% CAGR)' },
  NEGATIVE: { max: 0, label: 'Negative CAGR' },
} as const;

// Helper to get performance tier for a CAGR value
export function getPerformanceTier(cagr: number): keyof typeof PERFORMANCE_TIERS {
  if (cagr >= PERFORMANCE_TIERS.HIGH.min) return 'HIGH';
  if (cagr >= PERFORMANCE_TIERS.MEDIUM.min) return 'MEDIUM';
  if (cagr >= PERFORMANCE_TIERS.LOW.min) return 'LOW';
  return 'NEGATIVE';
}
